// components/CreateCompetition.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useCompetition } from '../contexts/CompetitionContext';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { getCompetitionById } from '../services/apiServices';
import { Calendar, Clock } from 'lucide-react';

export const CreateCompetition = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { createCompetition, loadCompetitionById, loading, error } = useCompetition();
    console.log(location.state, 'location.state');
    const competitionId = location.state?._id

    // Get current date and time in local timezone, rounded to nearest 15 minutes
    const getDefaultDateTime = () => {
        const now = new Date();
        // Round to nearest 15 minutes
        const minutes = Math.ceil(now.getMinutes() / 15) * 15;
        now.setMinutes(minutes);
        now.setSeconds(0);
        now.setMilliseconds(0);

        // Format as datetime-local input requires: YYYY-MM-DDTHH:mm
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const mins = String(now.getMinutes()).padStart(2, '0');

        return `${year}-${month}-${day}T${hours}:${mins}`;
    };

    const [formData, setFormData] = useState({
        name: '',
        category: '',
        numberOfBoulders: '4',
        climbTime: '240',
        warningTime: '60',
        wrapTime: '30',
        startDate: getDefaultDateTime(),
        toppoints: 25,
        zonepoints: 10,
        penalty: 0.1
    });

    const [boulders, setBoulders] = useState([
        { name: 'Boulder 1' },
        { name: 'Boulder 2' },
        { name: 'Boulder 3' },
        { name: 'Boulder 4' }
    ]);

    useEffect(() => {
        async function fetchCompetition() {
            try {
                let organiserId = JSON.parse(localStorage.getItem("loggedAdminInfo")) ? JSON.parse(localStorage.getItem("loggedAdminInfo")).organizerId : "";
                const response = await getCompetitionById(location.state?._id);

                if (!response || response === "false") {
                    return;
                } else {
                    const competitionData = await response.json();
                    console.log(competitionData, 'hvdfhdhb');

                    // Parse existing start date if available
                    let startDateTime = getDefaultDateTime();

                    if (competitionData.startDate) {
                        const existingDate = new Date(competitionData.startDate);
                        const year = existingDate.getFullYear();
                        const month = String(existingDate.getMonth() + 1).padStart(2, '0');
                        const day = String(existingDate.getDate()).padStart(2, '0');
                        const hours = String(existingDate.getHours()).padStart(2, '0');
                        const minutes = String(existingDate.getMinutes()).padStart(2, '0');
                        startDateTime = `${year}-${month}-${day}T${hours}:${minutes}`;
                    }

                    setFormData(prev => ({
                        ...prev,
                        name: competitionData?.name ? String(competitionData.name) : prev.name,
                        category: competitionData?.category ? String(competitionData.category) : prev.category,
                        numberOfBoulders: String(competitionData?.numberofboulders || 4),
                        climbTime: competitionData?.climbTime != null ? String(competitionData.climbTime) : prev.climbTime,
                        warningTime: competitionData?.warningTime != null ? String(competitionData.warningTime) : prev.warningTime,
                        wrapTime: competitionData?.wrapTime != null ? String(competitionData?.wrapTime) : prev.wrapTime,
                        startDate: startDateTime,
                        toppoints: competitionData?.toppoints ? competitionData.toppoints : 25,
                        zonepoints: competitionData?.zonepoints ? competitionData.zonepoints : 10,
                        penalty: competitionData?.penalty ? competitionData?.penalty : 0.1,
                        _id: competitionData?._id
                    }))

                    if (competitionData?.boulders) {
                        setBoulders(competitionData.boulders.map(boulder => ({
                            name: boulder.name,
                            judgeId: boulder.judgeId,
                            maxScore: boulder.maxScore,
                            timeLimit: boulder.timeLimit,
                            _id: boulder._id
                        })));
                    }
                }
            } catch (error) {
                console.error("Error fetching climbers:", error);
            }
        }
        if (location.state?._id) {
            fetchCompetition();
        }
    }, [location.state]);

    useEffect(() => {
        const boulderCount = parseInt(formData.numberOfBoulders, 10) || 4;
        const currentBoulderCount = boulders.length;

        if (boulderCount > currentBoulderCount) {
            const newBoulders = [...boulders];
            for (let i = currentBoulderCount; i < boulderCount; i++) {
                newBoulders.push({ name: `Boulder ${i + 1}` });
            }
            setBoulders(newBoulders);
        } else if (boulderCount < currentBoulderCount) {
            setBoulders(prev => prev.slice(0, boulderCount));
        }
    }, [formData.numberOfBoulders]);

    const handleInputChange = (field, value) => {
        if (['numberOfBoulders', 'climbTime', 'warningTime', 'wrapTime'].includes(field)) {
            value = value.replace(/[^0-9]/g, '');
        }

        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleBoulderNameChange = (index, value) => {
        console.log(boulders, 'boulders');

        const newBoulders = [...boulders];
        newBoulders[index] = {
            ...newBoulders[index],
            name: value
        };
        setBoulders(newBoulders);
    };

    const getBoulderNames = () => {
        return boulders.slice(0, parseInt(formData.numberOfBoulders, 10)).map(b => ({
            ...b,
            name: b.name
        }));
    };

    // Calculate end date based on start datetime and climb time
    const calculateEndDate = () => {
        if (!formData.startDate) return null;

        const startDateTime = new Date(formData.startDate);
        const climbTimeSeconds = parseInt(formData.climbTime) || 240;
        const endDateTime = new Date(startDateTime.getTime() + climbTimeSeconds * 1000);

        return endDateTime;
    };

    const validateForm = () => {
        const { name, category, numberOfBoulders, climbTime, warningTime, wrapTime, startDate } = formData;

        if (!name.trim()) {
            alert('Error: Please enter a round name');
            return false;
        }

        if (!category.trim()) {
            alert('Error: Please enter a category');
            return false;
        }

        const bouldersCount = parseInt(numberOfBoulders, 10);
        // if (isNaN(bouldersCount) || bouldersCount < 4 || bouldersCount > 6) {
        //     alert('Error: Number of boulders must be between 4 and 6');
        //     return false;
        // }

        if (isNaN(parseInt(climbTime, 10)) || isNaN(parseInt(warningTime, 10)) || isNaN(parseInt(wrapTime, 10))) {
            alert('Error: All time values must be valid numbers');
            return false;
        }

        if (!startDate) {
            alert('Error: Please select a start date and time');
            return false;
        }

        // Validate that start datetime is not in the past
        const startDateTime = new Date(startDate);
        if (startDateTime < new Date()) {
            const proceed = window.confirm('Warning: The selected start date/time is in the past. Do you want to continue?');
            if (!proceed) return false;
        }

        const emptyBoulderNames = boulders.slice(0, bouldersCount).filter(boulder => !boulder.name || !boulder.name.trim());
        if (emptyBoulderNames.length > 0) {
            alert('Error: All boulders must have names');
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        let organiserId = JSON.parse(localStorage.getItem("loggedAdminInfo")) ? JSON.parse(localStorage.getItem("loggedAdminInfo")).organizerId : "";

        // Calculate start and end dates
        const startDateTime = new Date(formData.startDate);
        const climbTimeSeconds = parseInt(formData.climbTime) || 240;
        const endDateTime = new Date(startDateTime.getTime() + climbTimeSeconds * 1000);

        const submissionData = {
            ...formData,
            startDate: startDateTime.toISOString(),
            endDate: endDateTime.toISOString(),
            boulderNames: getBoulderNames(),
            _id: competitionId || -1
        };

        console.log('Submitting data:', submissionData);
        console.log('Boulders data:', boulders.slice(0, parseInt(formData.numberOfBoulders, 10)));

        const result = await createCompetition(organiserId, submissionData);
        console.log(result, 'result');

        if (result) {
            console.log(result);
            navigate('/competitiondetails', {
                state: result,
            });
        }
    };

    // Get min datetime for datetime picker (current time)
    const getMinDateTime = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    const endDate = calculateEndDate();

    return (
        <div className="min-h-screen bg-gray-50 p-4 md:p-6 max-w-4xl mx-auto">
            <Card className="bg-white border-gray-200 shadow-lg">
                <CardHeader className="pb-6 border-b border-gray-200">
                    <CardTitle className="text-2xl font-bold text-gray-900">
                        {competitionId ? 'Edit Competition' : 'Create New Competition'}
                    </CardTitle>
                    <CardDescription className="text-gray-600 text-base mt-2">
                        Set up a new bouldering round with custom boulder names and timing settings
                    </CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                    <form onSubmit={handleSubmit} className="space-y-8">
                        {/* Competition Details */}
                        <div className="space-y-6">
                            <div className="space-y-3">
                                <h3 className="text-xl font-semibold text-gray-900">Competition Details</h3>
                                <p className="text-gray-600">Basic information about your competition round</p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-3">
                                    <Label htmlFor="name" className="text-gray-700 font-medium text-sm">
                                        Round Name
                                    </Label>
                                    <p className="text-xs text-gray-500">e.g., Finals, Qualifiers, Semi-Finals</p>
                                    <Input
                                        id="name"
                                        value={formData.name}
                                        onChange={(e) => handleInputChange('name', e.target.value)}
                                        placeholder="Enter round name"
                                        required
                                        className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 h-11"
                                    />
                                </div>

                                <div className="space-y-3">
                                    <Label htmlFor="category" className="text-gray-700 font-medium text-sm">
                                        Category
                                    </Label>
                                    <p className="text-xs text-gray-500">e.g., Male, Female, Youth, Open</p>
                                    <Input
                                        id="category"
                                        value={formData.category}
                                        onChange={(e) => handleInputChange('category', e.target.value)}
                                        placeholder="Enter category"
                                        required
                                        className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 h-11"
                                    />
                                </div>

                                <div className="space-y-3">
                                    <Label htmlFor="numberOfBoulders" className="text-gray-700 font-medium text-sm">
                                        Number of Boulders
                                    </Label>
                                    <Input
                                        id="numberOfBoulders"
                                        value={formData.numberOfBoulders}
                                        onChange={(e) => handleInputChange('numberOfBoulders', e.target.value)}
                                        required
                                        className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 h-11"
                                    />
                                </div>

                                {/* <div className="space-y-3">
                                    <Label htmlFor="startDateTime" className="text-gray-700 font-medium text-sm">
                                        Start Date & Time
                                    </Label>
                                    <div className="relative">
                                        <Input
                                            id="startDateTime"
                                            type="datetime-local"
                                            value={formData.startDateTime}
                                            onChange={(e) => handleInputChange('startDateTime', e.target.value)}
                                            min={getMinDateTime()}
                                            required
                                            className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 h-11"
                                        />
                                    </div>
                                </div> */}
                                <div className="space-y-3">
                                    <Label htmlFor="startDateTime" className="text-gray-700 font-medium text-sm">
                                        Start Date & Time
                                    </Label>
                                    <div className="relative">
                                        <Input
                                            id="startDate"
                                            type="datetime-local"
                                            value={formData.startDate}
                                            onChange={(e) => handleInputChange('startDate', e.target.value)}
                                            min={getMinDateTime()}
                                            required
                                            className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 h-11"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-6">
                            <div className="space-y-3">
                                <h3 className="text-xl font-semibold text-gray-900">Score Settings</h3>
                                <p className="text-gray-600">Configure score for the competition</p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div className="space-y-3">
                                    <Label htmlFor="climbTime" className="text-gray-700 font-medium text-sm">
                                        Top Points
                                    </Label>
                                    <Input
                                        id="toppoints"
                                        value={formData.toppoints}
                                        onChange={(e) => handleInputChange('toppoints', e.target.value)}
                                        required
                                        className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 h-11"
                                    />
                                </div>

                                <div className="space-y-3">
                                    <Label htmlFor="warningTime" className="text-gray-700 font-medium text-sm">
                                        Zone Points
                                    </Label>
                                    <Input
                                        id="zonepoints"
                                        value={formData.zonepoints}
                                        onChange={(e) => handleInputChange('zonepoints', e.target.value)}
                                        required
                                        className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 h-11"
                                    />
                                </div>

                                <div className="space-y-3">
                                    <Label htmlFor="wrapTime" className="text-gray-700 font-medium text-sm">
                                        Attempt penalty
                                    </Label>
                                    <Input
                                        id="penalty"
                                        value={formData.penalty}
                                        onChange={(e) => handleInputChange('penalty', e.target.value)}
                                        required
                                        className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 h-11"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Time Settings */}
                        <div className="space-y-6">
                            <div className="space-y-3">
                                <h3 className="text-xl font-semibold text-gray-900">Time Settings</h3>
                                <p className="text-gray-600">Configure timing for the competition</p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div className="space-y-3">
                                    <Label htmlFor="climbTime" className="text-gray-700 font-medium text-sm">
                                        Climb Time T1 (seconds)
                                    </Label>
                                    <p className="text-xs text-gray-500">Total climb time</p>
                                    <Input
                                        id="climbTime"
                                        value={formData.climbTime}
                                        onChange={(e) => handleInputChange('climbTime', e.target.value)}
                                        required
                                        className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 h-11"
                                    />
                                </div>

                                <div className="space-y-3">
                                    <Label htmlFor="warningTime" className="text-gray-700 font-medium text-sm">
                                        Warning Time T2 (seconds)
                                    </Label>
                                    <p className="text-xs text-gray-500">Warning threshold</p>
                                    <Input
                                        id="warningTime"
                                        value={formData.warningTime}
                                        onChange={(e) => handleInputChange('warningTime', e.target.value)}
                                        required
                                        className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 h-11"
                                    />
                                </div>

                                <div className="space-y-3">
                                    <Label htmlFor="wrapTime" className="text-gray-700 font-medium text-sm">
                                        Wrap Time W (seconds)
                                    </Label>
                                    <p className="text-xs text-gray-500">Ready time</p>
                                    <Input
                                        id="wrapTime"
                                        value={formData.wrapTime}
                                        onChange={(e) => handleInputChange('wrapTime', e.target.value)}
                                        required
                                        className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 h-11"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Boulder Names */}
                        <div className="space-y-6">
                            <div className="space-y-3">
                                <h3 className="text-xl font-semibold text-gray-900">Boulder Names</h3>
                                <p className="text-gray-600">Customize names for each boulder in the competition</p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {boulders.length > 0 && boulders.map((boulder, index) => (
                                    <div key={index} className="space-y-3 p-4 border border-gray-200 rounded-lg bg-gray-50">
                                        <Label
                                            htmlFor={`boulder-${index}`}
                                            className="text-gray-700 font-medium text-sm"
                                        >
                                            Boulder {index + 1}
                                        </Label>
                                        <Input
                                            id={`boulder-${index}`}
                                            value={boulder?.name}
                                            onChange={(e) => handleBoulderNameChange(index, e.target.value)}
                                            placeholder={`Boulder ${index + 1}`}
                                            className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 bg-white"
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Error Display */}
                        {error && (
                            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                                <p className="text-red-700 font-medium">{error}</p>
                            </div>
                        )}

                        {/* Form Actions */}
                        <div className="flex gap-4 pt-6 border-t border-gray-200">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => navigate('/dashboard')}
                                className="flex-1 h-12 border-gray-300 text-gray-700 hover:bg-gray-50 font-medium"
                                disabled={loading}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                className="flex-1 h-12 bg-blue-600 hover:bg-blue-700 text-white font-medium"
                                disabled={loading}
                            >
                                {loading ? (
                                    <span className="flex items-center justify-center">
                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        {competitionId ? 'Updating...' : 'Creating...'}
                                    </span>
                                ) : (
                                    competitionId ? 'Update Competition' : 'Create Competition'
                                )}
                            </Button>
                        </div>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
};